import { useEffect, useRef, useState } from "react";
import { queryPresentionSummary } from "@/services/EventService";
import { useNotify } from "@/contexts/NotifyContext";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { ANGKATAN_PERTAMA, ColourCodes } from "iasma-datatypes";
import { Pie, PieChart, ResponsiveContainer } from "recharts";
import ChartPresentionNames from "./ChartPresentionNames";
import { useNavigate } from "react-router-dom";

type dataType = {
	year: number;
	hadir: number;
	konfirmasi: number;
	color: string;
	fill?: string;
}

export default function ChartPresention({ eventId }: { eventId: number }) {
	const navigate = useNavigate();
	const { setToken, session } = useAuth();
	const { setNotify } = useNotify();

	const [data, setData] = useState<dataType[]>([]);
	const [totalHadir, setTotalHadir] = useState(0);
	const [labels, setLabels] = useState<string[]>([]);
	const [hadirs, setHadirs] = useState<number[]>([]);
	// const [colors, setColors] = useState<string[]>([]);
	const [showNames, setShowNames] = useState<number>(0);

	const rowref = useRef<null | HTMLTableCellElement>(null);

	useEffect(() => {
		async function fetchPresention() {
			try {
				setToken(axios);
				let recs = await queryPresentionSummary(eventId);
				console.log(recs);
				let result: dataType[] = [];
				let totalHadir = 0;
				let labels: string[] = [];
				let hadirs: number[] = [];
				let colors: string[] = [];

				for (let i = 0; i < recs.length; i++) {
					const rec = recs[i];
					totalHadir += rec.Hadir;
					const color = ColourCodes[rec.Graduation - ANGKATAN_PERTAMA + 1] || "#94a3b8";
					result.push({ year: rec.Graduation, konfirmasi: rec.Konfirmasi, hadir: rec.Hadir, color, fill: color });
					labels.push(rec.Graduation.toString());
					hadirs.push(rec.Hadir);
					colors.push(ColourCodes[rec.Graduation - 1958]);
				}
				setData(result);
				setLabels(labels);
				setHadirs(hadirs);
				// setColors(colors);
				setTotalHadir(totalHadir);
				console.log(result);
			} catch (error) {
				console.log(error);
				setNotify({ title: "Query Presention Fail", error, severity: 'error' });
			}
		}
		fetchPresention()
	}, []);

	useEffect(() => {
		rowref.current?.scrollIntoView({ behavior: 'smooth' })
	});

	function onShowNamesClose() {
		setShowNames(0)
	}

	function onShowNames(year: number) {
		setShowNames(year)
	}

	function newRow(i: number, onRowClick: (year: number) => void, rec: dataType) {
		const alumniGraduation = session?.User?.Alumni?.Graduation;
		const isCurrentUserGraduation = alumniGraduation !== undefined && alumniGraduation === (rec.year + 2);

		if (isCurrentUserGraduation) {
			return (
				<tr key={i} onClick={() => onRowClick(rec.year)} className="cursor-pointer hover:bg-slate-50">
					<td className="py-1 text-left">
						{rec.year}
						<a href={`/chart/confirm/${rec.year}`} className="ml-1 text-slate-500 hover:text-slate-700" aria-label={`Detail tahun ${rec.year}`}>
							<i className="fa fa-circle-info"></i>
						</a>
					</td>
					<td className="p-1 text-right">{rec.konfirmasi}</td>
					<td className="p-1 text-right" ref={rowref}>{rec.hadir}</td>
				</tr>
			);
		}

		return (
			<tr key={i} onClick={() => onRowClick(rec.year)} className="cursor-pointer hover:bg-slate-50">
				<td className="py-1 text-left">
					{rec.year}
					<a href={`/chart/confirm/${rec.year}`} className="ml-1 text-slate-500 hover:text-slate-700" aria-label={`Detail tahun ${rec.year}`}>
						<i className="fa fa-circle-info"></i>
					</a>
				</td>
				<td className="p-1 text-center text-nowrap">{rec.konfirmasi > 0 ? rec.konfirmasi + " urang" : "dak ado" }</td>
				<td className="p-1 text-right text-nowrap">{rec.hadir > 0 ? rec.hadir+ " urang" : "dak ado"}</td>
			</tr>
		);
	}

	const tableMaxHeight = window.innerHeight - 150;

	if (showNames >= ANGKATAN_PERTAMA) {
		return <ChartPresentionNames eventId={eventId} year={showNames} callback={onShowNamesClose} />
	} else {
		return (
			<div className="m-0 mt-0 max-w-full">
				<div className="w-full h-full mt-0 mb-0 p-1.25 relative content-center bg-white/50 overflow-auto">
					{data.length > 0 && (
						<div className="relative w-full">
							<ResponsiveContainer width="100%" height={260}>
								<PieChart>
									<Pie
										data={data}
										dataKey="hadir"
										nameKey="year"
										cx="50%"
										cy="50%"
										outerRadius={90}
										innerRadius={60}
										paddingAngle={0}
										stroke="none"
										label={({ year, hadir }: any) => (hadir ? String(year) : "")}
										labelLine={false}
									/>
								</PieChart>
							</ResponsiveContainer>
							<div className="pointer-events-none absolute inset-0 flex items-center justify-center">
								<div className="text-center">
									<div className="text-xl font-bold text-slate-800">{totalHadir}</div>
									<div className="text-[10px] uppercase tracking-wide text-slate-500">urang</div>
								</div>
							</div>
						</div>
					)}

					<table className="mb-2 mx-auto max-w-50 text-sm border-separate border-spacing-0">
						<thead className="sticky top-0 bg-gray-50">
							<tr className="">
								<th className="p-1 text-left font-semibold">Angkatan</th>
								<th className="p-1 text-center font-semibold">Konfirmasi</th>
								<th className="p-1 text-right font-semibold">Tibo</th>
							</tr>
						</thead>
						<tbody>
							{data.map((rec, i) => (newRow(i, onShowNames, rec)))}
						</tbody>
						<tfoot className="sticky bottom-0 bg-gray-50">
							<tr>
								<td className="p-1 mt-4 px-8 text[1.2em] italic text-left text-xs text-nowrap" colSpan={2}>({new Date().toLocaleString()})</td>
								<td className="p-1 text-right font-semibold text-nowrap">{totalHadir} urang</td>
							</tr>
						</tfoot>
					</table>
				</div>
				<div className="mt-4 px-8 text-[0.8em] italic text-center">klik dibaris angkatan untuak maliek namo nan alah tibo</div>
				<div className="w-3/4 mx-auto mt-3">
					<button type="button" onClick={() => navigate(-1)} className="w-full rounded-md mb-2.5 p-2.5 bg-[#f8ad3c] text-white cursor-pointer opacity-80 hover:opacity-100">
						Kembali
					</button>
				</div>
			</div>
		)
	}
}